import {TProfilesImportInProgress, TUrlImportItem} from "mapx-components/BulkImport/types";
import {createSelector} from "reselect";
import {bulkProfileImportsSelector} from "store/background-tasks/backgroundTaskSelectors";
import {companySelector} from "store/company/companySelectors";
import {projectSelector} from "store/project-list/projectListSelectors";
import {TRootState} from "types";

export const showBulkImportReportSelector = (state: TRootState) =>
	state.bulkImport.showBulkImportReport;

export const processedImportsSelectors = (state: TRootState) => state.bulkImport.processedImports;

export const showLastBulkImportReportSelector = (state: TRootState) =>
	state.bulkImport.showLastBulkImportReport;

export const showLastBulkImportReportForCompanySelector = (state: TRootState) =>
	state.bulkImport.showLastBulkImportReportForCompany;

export const isModalDisplayedForProject = (state: TRootState) =>
	state.bulkImport.activeAddPeopleModalForProject;

export const activeImportInfoForCompanySelector = (state: TRootState) =>
	state.bulkImport.bulkImportInfoOfCompany;

export const lastCompletedCandidateImportByLinkedInForProjectSelector = (state: TRootState) =>
	state.bulkImport.lastCompletedCandidateImportByLinkedInForProject;

export const candidatesImportForCompanySelector = (state: TRootState) =>
	state.bulkImport.candidateImportByLinkedInForCompany;

const getImportData = (importInfo: TProfilesImportInProgress[], importID: Nullable<number>) => {
	return importInfo?.find(
		(importData) => importData.purpose === "import" && importData?.project_id === importID,
	);
};

const getImportOrRefreshData = (
	importInfo: TProfilesImportInProgress[],
	importID: Nullable<number>,
) => {
	return importInfo?.find((importData) => importData?.project_id === importID);
};

export const currentProjectBulkImportURLsSelector = createSelector(
	[bulkProfileImportsSelector, projectSelector],
	(
		candidateImportsByLinkedInForProject: TProfilesImportInProgress[],
		project,
	): Nullable<TUrlImportItem[]> => {
		const projectImportData = getImportData(
			candidateImportsByLinkedInForProject,
			project?.id as number,
		);

		if (!projectImportData || !projectImportData?.linkedin_candidate_urls) {
			return null;
		}

		// Return the original array reference directly to preserve referential equality.
		// Creating new objects via .map() would produce a new reference on every call,
		// defeating reselect memoization and causing infinite re-renders in downstream useEffects.
		return projectImportData.linkedin_candidate_urls;
	},
);

export const currentCompanyBulkImportURLsSelector = createSelector(
	[bulkProfileImportsSelector, activeImportInfoForCompanySelector, companySelector],
	(
		candidateImportsByLinkedInForProject: TProfilesImportInProgress[],
		activeImport,
		currentCompany,
	): Nullable<TUrlImportItem[]> => {
		const companyImportData = getImportData(candidateImportsByLinkedInForProject, null);

		if (!companyImportData || !companyImportData?.linkedin_candidate_urls) {
			return null;
		}

		if (activeImport?.id !== currentCompany?.id) {
			return null;
		}

		return companyImportData.linkedin_candidate_urls;
	},
);

export const isImportInProgressForCurrentProject = createSelector(
	[processedImportsSelectors, projectSelector, bulkProfileImportsSelector],
	(processedImportsList, project, bulkProfileImports) => {
		const projectImportData = getImportData(bulkProfileImports, project?.id as number);
		const hasInProgressImport = projectImportData && projectImportData.status !== "waiting";

		return (
			(processedImportsList && processedImportsList.includes(project?.id as number)) ||
			hasInProgressImport
		);
	},
);

export const isImportOrRefreshInProgressForCurrentProject = createSelector(
	[processedImportsSelectors, projectSelector, bulkProfileImportsSelector],
	(processedImportsList, project, bulkProfileImports) => {
		const projectImportData = getImportOrRefreshData(bulkProfileImports, project?.id as number);
		const hasInProgressImport = projectImportData && projectImportData.status !== "waiting";

		return (
			(processedImportsList && processedImportsList.includes(project?.id as number)) ||
			hasInProgressImport
		);
	},
);

export const isImportInProgressForCurrentCompany = createSelector(
	[
		processedImportsSelectors,
		bulkProfileImportsSelector,
		activeImportInfoForCompanySelector,
		companySelector,
	],
	(processedImportsList, bulkProfileImports, activeImport, company) => {
		if (!activeImport || activeImport.id !== company?.id) {
			return false;
		}

		const companyImportData = getImportData(bulkProfileImports, null);
		const isImportInProgress =
			companyImportData?.status && companyImportData.status !== "waiting";

		return processedImportsList?.includes(0) || isImportInProgress;
	},
);

export const activeImportPercentageForCompanySelector = createSelector(
	[bulkProfileImportsSelector],
	(bulkProfileImports) => {
		const companyImportData = getImportData(bulkProfileImports, null);

		if (companyImportData) {
			return companyImportData.progress;
		} else {
			return 100;
		}
	},
);

export const isImportInProgressForNonCurrentCompany = createSelector(
	[
		processedImportsSelectors,
		bulkProfileImportsSelector,
		activeImportPercentageForCompanySelector,
		activeImportInfoForCompanySelector,
		companySelector,
	],
	(processedImportsList, bulkProfileImports, activeImportPercentage, activeImport, company) => {
		if (!activeImport || activeImport.id === company?.id) {
			return false;
		}

		const companyImportData = getImportData(bulkProfileImports, null);
		const isImportInProgress =
			companyImportData?.status && companyImportData.status !== "waiting";

		return (
			(processedImportsList?.includes(0) &&
				activeImportPercentage &&
				activeImportPercentage < 100) ||
			isImportInProgress
		);
	},
);

export const activeImportPercentageForProject = createSelector(
	[bulkProfileImportsSelector, projectSelector],
	(bulkProfileImports, project) => {
		const projectImportData = getImportData(bulkProfileImports, project?.id as number);

		if (projectImportData) {
			return projectImportData.progress;
		} else {
			return 100;
		}
	},
);

export const showBulkImportReportForProjectSelector = createSelector(
	[showBulkImportReportSelector, projectSelector],
	(showReport, project) => {
		if (showReport) {
			return showReport[project?.id as number] ?? false;
		} else {
			return false;
		}
	},
);

export const showBulkImportReportForCompanySelector = createSelector(
	[showBulkImportReportSelector],
	(showReport) => {
		if (showReport) {
			return showReport[0] ?? false;
		} else {
			return false;
		}
	},
);

// ============================================
// Company Bulk Import into Project Selectors
// ============================================

export const lastCompletedCompanyImportForProjectSelector = (state: TRootState) =>
	state.bulkImport.lastCompletedCompanyImportForProject;

const getCompanyImportData = (
	importInfo: TProfilesImportInProgress[],
	projectId: Nullable<number>,
) => {
	return importInfo?.find(
		(importData) =>
			importData.purpose === "import" &&
			(importData.import_type === "Companies Bulk Import" ||
				importData.type === "Companies Bulk Import") &&
			importData?.project_id === projectId,
	);
};

export const currentProjectCompanyBulkImportURLsSelector = createSelector(
	[bulkProfileImportsSelector, projectSelector],
	(bulkImports: TProfilesImportInProgress[], project): Nullable<TUrlImportItem[]> => {
		const companyImportData = getCompanyImportData(bulkImports, project?.id as number);

		if (!companyImportData) {
			return null;
		}

		const urls =
			companyImportData.linkedin_company_urls || companyImportData.linkedin_candidate_urls;

		return urls as Nullable<TUrlImportItem[]>;
	},
);

export const isCompanyImportInProgressForCurrentProject = createSelector(
	[processedImportsSelectors, projectSelector, bulkProfileImportsSelector],
	(processedImportsList, project, bulkProfileImports) => {
		const companyImportData = getCompanyImportData(bulkProfileImports, project?.id as number);
		const hasInProgressImport = companyImportData && companyImportData.status !== "waiting";

		return (
			(processedImportsList && processedImportsList.includes(project?.id as number)) ||
			hasInProgressImport
		);
	},
);

export const activeCompanyImportPercentageForProject = createSelector(
	[bulkProfileImportsSelector, projectSelector],
	(bulkProfileImports, project) => {
		const companyImportData = getCompanyImportData(bulkProfileImports, project?.id as number);

		if (companyImportData) {
			return companyImportData.progress;
		} else {
			return 100;
		}
	},
);

export const showCompanyBulkImportReportForProjectSelector = createSelector(
	[showBulkImportReportSelector, projectSelector],
	(showReport, project) => {
		if (showReport) {
			// Use a unique key to avoid clashing with candidate reports
			// We use negative project ID as a convention for company import reports
			const companyReportKey = -(project?.id as number);

			return showReport[companyReportKey] ?? false;
		} else {
			return false;
		}
	},
);

// ============================================
// Company Bulk Import into Market Map Selectors
// ============================================

const getCompanyImportDataByMarketMap = (
	importInfo: TProfilesImportInProgress[],
	marketMapId: number,
) => {
	return importInfo?.find(
		(importData) =>
			importData.purpose === "import" &&
			(importData.import_type === "Companies Bulk Import" ||
				importData.type === "Companies Bulk Import") &&
			importData?.market_map_id === marketMapId,
	);
};

export const makeMarketMapCompanyImportURLsSelector = (marketMapId: number) =>
	createSelector(
		[bulkProfileImportsSelector],
		(bulkImports: TProfilesImportInProgress[]): Nullable<TUrlImportItem[]> => {
			const importData = getCompanyImportDataByMarketMap(bulkImports, marketMapId);

			if (!importData) return null;

			return (importData.linkedin_company_urls ||
				importData.linkedin_candidate_urls) as Nullable<TUrlImportItem[]>;
		},
	);

export const makeIsCompanyImportInProgressForMarketMap = (marketMapId: number) =>
	createSelector(
		[processedImportsSelectors, bulkProfileImportsSelector],
		(processedImportsList, bulkProfileImports) => {
			const importData = getCompanyImportDataByMarketMap(bulkProfileImports, marketMapId);
			const hasInProgressImport = importData && importData.status !== "waiting";

			return (
				(processedImportsList && processedImportsList.includes(marketMapId)) ||
				hasInProgressImport
			);
		},
	);

export const makeActiveCompanyImportPercentageForMarketMap = (marketMapId: number) =>
	createSelector([bulkProfileImportsSelector], (bulkProfileImports) => {
		const importData = getCompanyImportDataByMarketMap(bulkProfileImports, marketMapId);

		return importData ? importData.progress : 100;
	});

export const makeShowCompanyBulkImportReportForMarketMap = (marketMapId: number) =>
	createSelector([showBulkImportReportSelector], (showReport) => {
		if (!showReport) return false;

		// Mirror the same negative-ID convention used for project company imports
		return showReport[-marketMapId] ?? false;
	});
